import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import {
	DOMAIN_PHYSICAL,
	DOMAIN_SHOOTING,
	hasDomain,
} from "../lib/domainAccess";
import { useLanguage } from "../contexts/LanguageContext";
import { useNotificationCounts } from "../contexts/NotificationCountsContext";
import { LanguageToggle } from "./LanguageToggle";
import { FontSizeToggle } from "./FontSizeToggle";
import { ThemeToggle } from "./ThemeToggle";
import { MobileInstallPrompt } from "./MobileInstallPrompt";
import { PushOptInModal } from "./PushOptInModal";
import { DesktopSidebar } from "./DesktopSidebar";
import { CommandPalette } from "./CommandPalette";
import { usePushOptIn } from "../lib/usePushOptIn";
import { AI_TRAINING_DOCS_VISIBLE } from "../lib/featureFlags";
import { Button } from "./ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "./ui/avatar";
import { Badge } from "./ui/badge";
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuLabel,
	DropdownMenuSeparator,
	DropdownMenuTrigger,
} from "./ui/dropdown-menu";
import {
	Activity,
	Home,
	Calendar,
	Trophy,
	User,
	Settings,
	LogOut,
	Menu,
	X,
	Users,
	BookOpen,
	Bot,
	Brain,
	BarChart3,
	ClipboardList,
	Shield,
	Mail,
	Inbox,
	Zap,
	Globe,
	Repeat,
	UserCog,
	Check,
	Type,
	SunMoon,
	CreditCard,
	Crosshair,
	Receipt,
	Search,
	HeartPulse,
	Building2,
	SlidersHorizontal,
	Sparkles,
} from "lucide-react";
import { useState, useEffect, useRef } from "react";
import { fetchAPI, getToday } from "../lib/utils";
import { toast } from "sonner";

// Persisted across Layout remounts (each route renders its own <Layout>), so
// the bottom-nav indicator can animate from the previously active tab instead
// of snapping straight to the new position on every navigation.
let lastMobileNavIndex = -1;

const SIDEBAR_PINNED_KEY = "kraveon:sidebar-pinned";

const isMac =
	typeof navigator !== "undefined" &&
	/Mac|iPhone|iPad/.test(navigator.platform);

export function Layout({ children }) {
	const { user, logout, switchAccount, switchRole } = useAuth();
	const { t } = useLanguage();
	const location = useLocation();
	const navigate = useNavigate();
	const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
	const [userMenuOpen, setUserMenuOpen] = useState(false);
	const [commit, setCommit] = useState(null);
	const [coachHasAiDocsAccess, setCoachHasAiDocsAccess] = useState(false);
	const [switchingAccountId, setSwitchingAccountId] = useState(null);
	const [switchingRole, setSwitchingRole] = useState(null);
	const [pushOnboardingBadge, setPushOnboardingBadge] = useState(false);
	const [wellnessReminderDue, setWellnessReminderDue] = useState(false);
	const [commandOpen, setCommandOpen] = useState(false);
	const [sidebarPinned, setSidebarPinned] = useState(() => {
		try {
			const stored = localStorage.getItem(SIDEBAR_PINNED_KEY);
			return stored === null ? true : stored === "true";
		} catch {
			return true;
		}
	});
	const mainRef = useRef(null);
	const pushOptIn = usePushOptIn();
	const {
		messageUnreadCount,
		feedbackUnreadCount,
		refreshAllNotificationCounts,
	} = useNotificationCounts();
	const backendUrl =
		process.env.REACT_APP_BACKEND_URL || "http://localhost:8001";
	const userAvatarUrl = (() => {
		const raw = user?.picture;
		if (!raw) return null;
		if (raw.startsWith("http")) return raw;
		if (raw.startsWith("/api/")) return `${backendUrl}${raw}`;
		if (raw.startsWith("/uploads/")) return `${backendUrl}/api${raw}`;
		return raw;
	})();

	// Use is_admin flag from backend instead of hardcoded list
	const isAdmin = user?.is_admin || user?.role === "admin";
	const isCoachOrAdmin = user?.role === "coach" || isAdmin;

	useEffect(() => {
		loadCommit();
		if (isCoachOrAdmin) {
			loadAiDocsAccess();
		}
	}, [isCoachOrAdmin]);

	useEffect(() => {
		if (!user?.user_id) {
			setPushOnboardingBadge(false);
			return;
		}

		let cancelled = false;
		const loadPushOnboardingState = async () => {
			try {
				const [prefs, pushCfg] = await Promise.all([
					fetchAPI("/users/me/notifications").catch(() => null),
					fetchAPI("/users/me/push-config").catch(() => null),
				]);
				if (cancelled) return;
				const canPush = !!pushCfg?.push_available;
				const pushEverEnabled =
					!!prefs?.push_ever_enabled || !!prefs?.push_enabled;
				const showBadge = canPush && !pushEverEnabled;
				setPushOnboardingBadge(showBadge);
			} catch {
				if (!cancelled) setPushOnboardingBadge(false);
			}
		};

		const handlePushOnboardingUpdated = () => {
			loadPushOnboardingState();
		};

		loadPushOnboardingState();
		window.addEventListener(
			"push-onboarding-updated",
			handlePushOnboardingUpdated,
		);
		return () => {
			cancelled = true;
			window.removeEventListener(
				"push-onboarding-updated",
				handlePushOnboardingUpdated,
			);
		};
	}, [user?.user_id, location.pathname]);

	useEffect(() => {
		requestAnimationFrame(() => {
			if (mainRef.current) {
				mainRef.current.scrollTo(0, 0);
			}
		});
	}, [location.pathname]);

	useEffect(() => {
		// Defensive cleanup for potential stale overlay/body locks (popover/dialog)
		// after route transitions on mobile Safari/back navigation.
		document.body.style.removeProperty("pointer-events");
		document.body.style.removeProperty("overflow");
		document.body.removeAttribute("data-scroll-locked");
	}, [location.pathname]);

	// Wellness reminder: show a nav badge when today's diary is still to be
	// filled (and the coach hasn't muted the reminder). Refreshes on navigation
	// and when the diary page signals a change.
	useEffect(() => {
		if (!user?.user_id || user?.role !== "athlete") {
			setWellnessReminderDue(false);
			return undefined;
		}
		let cancelled = false;
		const loadStatus = async () => {
			try {
				const status = await fetchAPI(
					`/wellness/me/status?date=${getToday()}`,
				).catch(() => null);
				if (!cancelled) setWellnessReminderDue(!!status?.reminder_due);
			} catch {
				if (!cancelled) setWellnessReminderDue(false);
			}
		};
		loadStatus();
		window.addEventListener("wellness-updated", loadStatus);
		return () => {
			cancelled = true;
			window.removeEventListener("wellness-updated", loadStatus);
		};
	}, [user?.user_id, user?.role, location.pathname]);

	useEffect(() => {
		refreshAllNotificationCounts();
	}, [location.pathname, refreshAllNotificationCounts]);

	const loadCommit = async () => {
		try {
			const data = await fetchAPI("/meta/version");
			setCommit(data.commit || null);
		} catch (e) {
			setCommit(null);
		}
	};

	const loadAiDocsAccess = async () => {
		try {
			const data = await fetchAPI("/ai-training-docs/coach-access");
			setCoachHasAiDocsAccess(data.has_access);
		} catch (e) {}
	};

	const handleLogout = async () => {
		await logout();
		navigate("/login");
	};

	const linkedAccounts = Array.isArray(user?.linked_accounts)
		? user.linked_accounts
		: [];
	const hasLinkedAccounts = linkedAccounts.length > 0;

	// Dual-role account: one account that can act as both athlete and coach.
	// `role` from the backend is already the effective (active) mode.
	const availableRoles = Array.isArray(user?.available_roles)
		? user.available_roles
		: [];
	const hasRoleModes = availableRoles.length > 1;
	const roleModeLabel = (role) =>
		role === "coach" ? t("roleModeCoach") : t("roleModeAthlete");
	// Il ruolo di un account collegato, nelle parole dell'app: "athlete" grezzo
	// in un menu italiano non si legge (NN/g H2).
	const accountRoleLabel = (role) =>
		role === "coach" || role === "athlete" ? roleModeLabel(role) : role;

	const handleSwitchRole = async (nextRole) => {
		if (!nextRole || switchingRole || nextRole === user?.role) return;
		setUserMenuOpen(false);
		setMobileMenuOpen(false);
		setSwitchingRole(nextRole);
		try {
			await switchRole(nextRole);
			toast.success(t("roleSwitchSuccess"));
			// The current route may belong to the mode we just left (it would now
			// answer 403), so land on the dashboard exactly like an account switch.
			navigate("/dashboard", { replace: true });
		} catch (error) {
			toast.error(error?.message || t("roleSwitchError"));
		} finally {
			setSwitchingRole(null);
		}
	};

	const handleSwitchAccount = async (targetUserId) => {
		if (!targetUserId || switchingAccountId) return;
		setUserMenuOpen(false);
		setMobileMenuOpen(false);
		setSwitchingAccountId(targetUserId);
		try {
			await switchAccount(targetUserId);
			toast.success(t("accountSwitchSuccess"));
			navigate("/dashboard", { replace: true });
		} catch (error) {
			toast.error(error?.message || t("accountSwitchError"));
		} finally {
			setSwitchingAccountId(null);
		}
	};

	const coachLinks = [
		{ href: "/dashboard", label: t("dashboard"), icon: Home },
		{ href: "/athletes", label: t("athletes"), icon: Users },
		{
			href: "/feedback",
			label: t("feedback"),
			icon: Inbox,
			badge: feedbackUnreadCount,
		},
		{ href: "/monitoring", label: t("monitoring"), icon: Activity },
		{ href: "/competition-hub", label: t("competitionHub"), icon: Trophy },
		{
			href: "/shooting-drills",
			label: t("shootingDrills"),
			icon: Crosshair,
			domain: DOMAIN_SHOOTING,
		},
		// Compare solo a chi amministra almeno una disciplina: per un coach
		// normale non esiste.
		...((user?.reference_admin_disciplines || []).length > 0 && !isAdmin
			? [
					{
						href: "/shooting-admin",
						label: t("shootingConfig"),
						icon: Settings,
						domain: DOMAIN_SHOOTING,
					},
				]
			: []),
		{
			href: "/shooting-leaderboards",
			label: t("shootingLeaderboards"),
			icon: Trophy,
			domain: DOMAIN_SHOOTING,
		},
		{ href: "/programs", label: t("programs"), icon: ClipboardList },
		{ href: "/exercises", label: t("exercises"), icon: BookOpen },
		{
			href: "/conditioning-library",
			label: t("conditioning"),
			icon: Zap,
			domain: DOMAIN_PHYSICAL,
		},
		// Amministrazione dei propri coach team: sta in "Gestione" accanto a
		// Incassi, non in "Coaching", perche' non e' lavoro quotidiano
		// sull'atleta (docs/coach-teams/PLAN.md §3).
		{ href: "/coach-teams", label: t("coachTeams"), icon: Building2 },
		// Sport seguiti e modelli prestativi propri (docs/sport-config, Fase 2):
		// configurazione, non lavoro quotidiano, quindi in "Gestione".
		{ href: "/sport-config", label: t("sportConfigNav"), icon: SlidersHorizontal },
		{ href: "/billing", label: t("billing"), icon: CreditCard },
		// Configurazione AI: chiave dell'assistente in-app + connettore MCP.
		// Sta accanto ai Documenti AI, nello stesso gruppo di navigazione.
		{ href: "/ai-assistant", label: t("aiCoach"), icon: Bot },
		// Cosa si puo' chiedere all'assistente, con esempi da copiare: la
		// pagina sopra dice come si collega, questa perche'.
		{ href: "/ai-guide", label: t("aiGuideNav"), icon: Sparkles },
		// Documenti formativi: nascosti finche' AI_TRAINING_DOCS_VISIBLE resta
		// spento (frontend/src/lib/featureFlags.js). L'accesso per-coach si
		// continua a leggere, perche' e' quello che decide la voce il giorno
		// che tornano visibili.
		...(AI_TRAINING_DOCS_VISIBLE && coachHasAiDocsAccess
			? [{ href: "/ai-training-docs", label: t("aiDocs"), icon: Brain }]
			: []),
		{
			href: "/messages",
			label: t("messages"),
			icon: Mail,
			badge: messageUnreadCount,
		},
	];

	const athleteLinks = [
		{ href: "/dashboard", label: t("dashboard"), icon: Home },
		{ href: "/performance", label: t("performance"), icon: BarChart3 },
		// Le classifiche di tiro globali restano **solo lato coach** per ora.
		// Quelle che servono all'atleta sono le due che esistono già per il
		// fisico — profilo prestativo e classifica del giorno sull'esercizio —
		// non una pagina in più. Questa si valuta quando ci saranno dati veri.
		...(user?.wellness_enabled
			? [
					{
						href: "/wellness",
						label: t("wellnessDiary"),
						icon: HeartPulse,
						alert: wellnessReminderDue,
					},
				]
			: [{ href: "/workouts", label: t("diary"), icon: Calendar }]),
		{
			href: "/messages",
			label: t("messages"),
			icon: Mail,
			badge: messageUnreadCount,
		},
		{ href: "/profile", label: t("profile"), icon: User },
		{ href: "/my-payments", label: t("payments"), icon: Receipt },
	];

	// Add admin link for admins
	const adminLink = { href: "/admin", label: "Admin", icon: Shield };

	let navLinks;
	if (isAdmin) {
		// Admin has coach links + admin panel
		navLinks = [adminLink, ...coachLinks];
	} else if (user?.role === "coach") {
		navLinks = coachLinks;
	} else {
		navLinks = athleteLinks;
	}

	// Practice-domain gating (see lib/domainAccess.js). Entries without a
	// `domain` belong to everyone; an empty `user.domains` means no restriction,
	// so nothing disappears until an admin says so. This hides the *entry point*
	// only — the routes themselves stay reachable, so a shared link never 404s.
	navLinks = navLinks.filter(
		(link) => !link.domain || hasDomain(user, link.domain),
	);

	// Athlete: 5 tabs (incl. Messaggi). Pagamenti resta solo nel menu (non nella bottom-nav).
	// Coach/Admin mobile bottom nav: Dashboard, Atleti, Feedback, Programmi, Messaggi.
	// Admin panel stays in the menu.
	const coachMobileNavLinks = coachLinks.filter((link) =>
		["/dashboard", "/athletes", "/feedback", "/programs", "/messages"].includes(
			link.href,
		),
	);
	// Athlete bottom nav caps at 5. Keep the health diary, messages and profile
	// visible whether or not tracking has been enabled yet.
	const pickAthleteMobileLinks = (hrefs) =>
		hrefs
			.map((href) => navLinks.find((link) => link.href === href))
			.filter(Boolean);
	const athleteMobileNavLinks = pickAthleteMobileLinks([
		"/dashboard",
		"/performance",
		user?.wellness_enabled ? "/wellness" : "/workouts",
		"/messages",
		"/profile",
	]);
	const mobileNavLinks =
		user?.role === "athlete" ? athleteMobileNavLinks : coachMobileNavLinks;

	// Desktop sidebar (coach/admin): every entry stays visible, grouped by
	// area instead of being cut off by an "Altro" overflow menu.
	const NAV_GROUP_HREFS = [
		{
			id: "coaching",
			label: t("navGroupCoaching"),
			hrefs: ["/dashboard", "/athletes", "/feedback", "/monitoring", "/messages"],
		},
		{
			id: "training",
			label: t("navGroupTraining"),
			hrefs: [
				"/programs",
				"/exercises",
				"/conditioning-library",
				"/competition-hub",
				"/shooting-drills",
				"/shooting-leaderboards",
				"/shooting-admin",
			],
		},
		{
			id: "ai",
			label: t("navGroupAi"),
			hrefs: AI_TRAINING_DOCS_VISIBLE
				? ["/ai-assistant", "/ai-guide", "/ai-training-docs"]
				: ["/ai-assistant", "/ai-guide"],
		},
		{
			id: "management",
			label: t("navGroupManagement"),
			hrefs: ["/coach-teams", "/sport-config", "/billing", "/admin"],
		},
	];

	const linksByHref = new Map(navLinks.map((link) => [link.href, link]));
	const groupedHrefs = new Set(NAV_GROUP_HREFS.flatMap((g) => g.hrefs));
	const navGroups = NAV_GROUP_HREFS.map((group) => ({
		id: group.id,
		label: group.label,
		links: group.hrefs.map((href) => linksByHref.get(href)).filter(Boolean),
	}))
		// Any link not explicitly grouped (e.g. a newly added route) still shows
		// up, appended to the last group, so nothing can silently disappear.
		.concat(
			navLinks.some((link) => !groupedHrefs.has(link.href))
				? [
						{
							id: "other",
							label: t("navMenu"),
							links: navLinks.filter((link) => !groupedHrefs.has(link.href)),
						},
					]
				: [],
		)
		.filter((group) => group.links.length > 0);

	const handleSidebarPinnedChange = (next) => {
		setSidebarPinned(next);
		try {
			localStorage.setItem(SIDEBAR_PINNED_KEY, String(next));
		} catch {}
	};

	// ⌘K / Ctrl+K opens the palette from anywhere in the app.
	useEffect(() => {
		if (!isCoachOrAdmin) return undefined;
		const onKeyDown = (event) => {
			if (
				event.key?.toLowerCase() === "k" &&
				(event.metaKey || event.ctrlKey)
			) {
				event.preventDefault();
				setCommandOpen((open) => !open);
			}
		};
		window.addEventListener("keydown", onKeyDown);
		return () => window.removeEventListener("keydown", onKeyDown);
	}, [isCoachOrAdmin]);

	const activeMobileIndex = mobileNavLinks.findIndex(
		(link) => location.pathname === link.href,
	);

	// Start the indicator at the previously active tab (persisted across the
	// per-route Layout remount), then slide it to the current tab on the next
	// frame so the CSS transition actually fires (smooth Apple/Instagram feel).
	const [indicatorIndex, setIndicatorIndex] = useState(
		lastMobileNavIndex >= 0 ? lastMobileNavIndex : activeMobileIndex,
	);
	useEffect(() => {
		if (activeMobileIndex < 0) return undefined;
		const raf = requestAnimationFrame(() => {
			setIndicatorIndex(activeMobileIndex);
			lastMobileNavIndex = activeMobileIndex;
		});
		return () => cancelAnimationFrame(raf);
	}, [activeMobileIndex]);

	return (
		<div className="layout-shell flex flex-col bg-background overflow-hidden">
			{/* Desktop Header */}
			<header className="glass-header sticky top-0 z-50 hidden md:block">
				<div
					className={`${
						isCoachOrAdmin ? "w-full" : "max-w-7xl mx-auto"
					} px-6 h-16 flex items-center justify-between`}
				>
					<div className="flex items-center gap-8">
						<Link
							to="/dashboard"
							className="flex items-center gap-3"
							data-testid="logo-link"
						>
							<img
								src="/logo-kraveon.svg"
								alt="Kraveon"
								className="w-9 h-9 rounded-lg"
							/>
							<span className="font-heading text-lg tracking-tight">
								KRAVEON
							</span>
						</Link>

						{/* Athletes keep the inline top nav (few entries, always fits).
						    Coach/admin navigate from the DesktopSidebar instead. */}
						{!isCoachOrAdmin && (
							<nav className="flex items-center gap-1">
								{navLinks.map((link) => {
									const Icon = link.icon;
									const isActive = location.pathname === link.href;
									return (
										<Link
											key={link.href}
											to={link.href}
											className={`flex items-center gap-2 px-3 py-2 rounded-sm text-sm font-medium transition-colors relative ${
												isActive
													? "bg-volt/10 text-volt"
													: "text-muted-foreground hover:text-foreground hover:bg-white/5"
											}`}
											data-testid={`nav-${link.href.slice(1)}`}
										>
											<Icon className="w-4 h-4" />
											<span className="whitespace-nowrap">{link.label}</span>
											{link.badge > 0 && (
												<Badge className="absolute -top-1 -right-1 h-5 min-w-[20px] flex items-center justify-center bg-blaze text-white text-xs p-0">
													{link.badge}
												</Badge>
											)}
											{link.alert && (
												<span className="absolute -top-1 -right-1 h-4 w-4 flex items-center justify-center bg-amber-500 text-black text-[10px] font-bold rounded-full">
													!
												</span>
											)}
										</Link>
									);
								})}
							</nav>
						)}
					</div>

					<div className="flex items-center gap-2">
						{isCoachOrAdmin && (
							<button
								type="button"
								onClick={() => setCommandOpen(true)}
								className="flex items-center gap-2 h-9 pl-3 pr-2 rounded-sm border border-border bg-white/[0.03] text-muted-foreground hover:text-foreground hover:border-volt/40 transition-colors"
								data-testid="command-palette-trigger"
							>
								<Search className="w-4 h-4" />
								<span className="text-sm">{t("navSearch")}</span>
								<kbd className="ml-1 px-1.5 py-0.5 rounded-[3px] border border-border bg-white/5 text-[10px] font-mono leading-none">
									{isMac ? "⌘K" : "Ctrl K"}
								</kbd>
							</button>
						)}

						<DropdownMenu open={userMenuOpen} onOpenChange={setUserMenuOpen}>
							<DropdownMenuTrigger asChild>
								<Button
									variant="ghost"
									className="flex items-center gap-3 h-10 px-3"
									data-testid="user-menu-trigger"
								>
									<Avatar className="w-8 h-8">
										<AvatarImage src={userAvatarUrl} />
										<AvatarFallback className="bg-volt text-white text-sm">
											{user?.name?.charAt(0).toUpperCase()}
										</AvatarFallback>
									</Avatar>
									<span className="text-sm font-medium">{user?.name}</span>
								</Button>
							</DropdownMenuTrigger>
							<DropdownMenuContent
								align="end"
								className="w-56 bg-card border-border"
							>
								<DropdownMenuLabel className="font-normal">
									<div className="flex flex-col space-y-1">
										<p className="text-sm font-medium">{user?.name}</p>
										<p className="text-xs text-muted-foreground">
											{user?.email}
										</p>
										<p className="text-xs text-volt uppercase">{user?.role}</p>
										{commit && (
											<p className="text-xs text-muted-foreground">
												Build:{" "}
												{commit === "unknown" ? "unknown" : commit.slice(0, 7)}
											</p>
										)}
									</div>
								</DropdownMenuLabel>
								{hasRoleModes && (
									<>
										<DropdownMenuSeparator className="bg-border" />
										<DropdownMenuLabel className="text-xs uppercase tracking-widest text-muted-foreground">
											{t("switchRoleLabel")}
										</DropdownMenuLabel>
										{availableRoles.map((role) => (
											<DropdownMenuItem
												key={role}
												onClick={() => handleSwitchRole(role)}
												disabled={switchingRole === role || user?.role === role}
												className="cursor-pointer"
											>
												{user?.role === role ? (
													<Check className="mr-2 h-4 w-4 text-volt" />
												) : (
													<UserCog className="mr-2 h-4 w-4" />
												)}
												<span>{roleModeLabel(role)}</span>
											</DropdownMenuItem>
										))}
									</>
								)}
								{hasLinkedAccounts && (
									<>
										<DropdownMenuSeparator className="bg-border" />
										<DropdownMenuLabel className="text-xs uppercase tracking-widest text-muted-foreground">
											{t("switchAccountLabel")}
										</DropdownMenuLabel>
										{linkedAccounts.map((account) => (
											<DropdownMenuItem
												key={account.user_id}
												onClick={() => handleSwitchAccount(account.user_id)}
												disabled={switchingAccountId === account.user_id}
												className="cursor-pointer"
											>
												<Repeat className="mr-2 h-4 w-4" />
												<div className="flex flex-col leading-tight">
													<span>{account.name || account.email}</span>
													<span className="text-[11px] text-muted-foreground uppercase">
														{accountRoleLabel(account.role)}
													</span>
												</div>
											</DropdownMenuItem>
										))}
									</>
								)}
								<DropdownMenuSeparator className="bg-border" />
								<div className="px-2 py-1.5 flex items-center gap-2">
									<Globe className="h-4 w-4 text-muted-foreground flex-shrink-0" />
									<LanguageToggle />
								</div>
								<div className="px-2 py-1.5 flex items-center gap-2">
									<Type className="h-4 w-4 text-muted-foreground flex-shrink-0" />
									<FontSizeToggle />
								</div>
								<div className="px-2 py-1.5 flex items-center gap-2">
									<SunMoon className="h-4 w-4 text-muted-foreground flex-shrink-0" />
									<ThemeToggle />
								</div>
								<DropdownMenuSeparator className="bg-border" />
								<DropdownMenuItem asChild>
									<Link
										to="/settings"
										className="flex items-center cursor-pointer justify-between w-full"
									>
										<span className="flex items-center">
											<Settings className="mr-2 h-4 w-4" />
											{t("settings")}
										</span>
									</Link>
								</DropdownMenuItem>
								<DropdownMenuSeparator className="bg-border" />
								<DropdownMenuItem
									onClick={() => {
										setUserMenuOpen(false);
										handleLogout();
									}}
									className="text-blaze cursor-pointer"
									data-testid="logout-btn"
								>
									<LogOut className="mr-2 h-4 w-4" />
									{t("logout")}
								</DropdownMenuItem>
							</DropdownMenuContent>
						</DropdownMenu>
					</div>
				</div>
			</header>

			{/* Mobile Header */}
			<header className="glass-header sticky top-0 z-50 md:hidden mobile-header-safe">
				<div className="px-4 h-14 flex items-center justify-between">
					<Link to="/dashboard" className="flex items-center gap-2">
						<img
							src="/logo-kraveon.svg"
							alt="Kraveon"
							className="w-8 h-8 rounded-lg"
						/>
						<span className="font-heading text-base tracking-tight">
							KRAVEON
						</span>
					</Link>

					<Button
						variant="ghost"
						size="icon"
						onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
						className={`text-muted-foreground hover:text-foreground hover:bg-white/10 active:bg-white/10 ${
							mobileMenuOpen ? "bg-white/10 text-foreground" : ""
						}`}
						data-testid="mobile-menu-toggle"
					>
						{mobileMenuOpen ? (
							<X className="w-5 h-5" />
						) : (
							<Menu className="w-5 h-5" />
						)}
					</Button>
				</div>

				{/* Mobile Menu Dropdown */}
				{mobileMenuOpen && (
					<div className="absolute mobile-menu-safe left-0 right-0 bg-card border-b border-border animate-fadeIn max-h-[calc(100svh-3.5rem)] overflow-y-auto overscroll-contain pb-4">
						{/* User identity header */}
						<div className="flex items-center gap-3 px-4 py-3 border-b border-border">
							<Avatar className="w-10 h-10 flex-shrink-0">
								<AvatarImage src={userAvatarUrl} />
								<AvatarFallback className="bg-volt text-white text-sm">
									{user?.name?.charAt(0).toUpperCase()}
								</AvatarFallback>
							</Avatar>
							<div className="min-w-0 flex-1">
								<p className="text-sm font-medium truncate">{user?.name}</p>
								<p className="text-xs text-muted-foreground truncate">
									{user?.email}
								</p>
								<p className="text-[10px] text-volt uppercase tracking-widest">
									{user?.role}
								</p>
							</div>
						</div>
						{/* Modalita' e account collegati in testa, accanto all'identita': e' il
						    gesto che cambia tutto il resto del menu, quindi non puo' stare in
						    fondo a una lista che sul telefono va scorsa (NN/g H6, H7). */}
						{(hasRoleModes || hasLinkedAccounts) && (
							<div
								className="px-4 py-3 border-b border-border space-y-3"
								data-testid="mobile-account-switch"
							>
								{hasRoleModes && (
									<div>
										<p className="text-[10px] uppercase tracking-widest text-muted-foreground mb-2">
											{t("switchRoleLabel")}
										</p>
										<div
											role="radiogroup"
											aria-label={t("switchRoleLabel")}
											className="grid grid-cols-2 gap-1 p-1 rounded-sm border border-border bg-white/[0.03]"
										>
											{availableRoles.map((role) => {
												const isActiveRole = user?.role === role;
												return (
													<button
														key={role}
														type="button"
														role="radio"
														aria-checked={isActiveRole}
														onClick={() => handleSwitchRole(role)}
														disabled={switchingRole === role || isActiveRole}
														className={`min-h-[44px] flex items-center justify-center gap-2 rounded-sm text-sm font-medium transition-colors ${
															isActiveRole
																? "bg-volt/15 text-volt"
																: "text-muted-foreground hover:text-foreground hover:bg-white/5"
														}`}
														data-testid={`mobile-role-${role}`}
													>
														{isActiveRole ? (
															<Check className="w-4 h-4" />
														) : (
															<UserCog className="w-4 h-4" />
														)}
														{roleModeLabel(role)}
													</button>
												);
											})}
										</div>
									</div>
								)}
								{hasLinkedAccounts && (
									<div>
										<p className="text-[10px] uppercase tracking-widest text-muted-foreground mb-2">
											{t("switchAccountLabel")}
										</p>
										<div className="space-y-1.5">
											{linkedAccounts.map((account) => (
												<button
													key={account.user_id}
													type="button"
													onClick={() => handleSwitchAccount(account.user_id)}
													disabled={switchingAccountId === account.user_id}
													className="w-full min-h-[44px] flex items-center gap-3 px-3 py-2 rounded-sm text-sm text-left border border-border text-muted-foreground hover:text-foreground hover:border-volt/40"
												>
													<Repeat className="w-4 h-4 flex-shrink-0" />
													<span className="flex-1 truncate">
														{account.name || account.email}
													</span>
													<span className="text-[10px] uppercase tracking-widest">
														{accountRoleLabel(account.role)}
													</span>
												</button>
											))}
										</div>
									</div>
								)}
							</div>
						)}
						<nav className="flex flex-col gap-1.5 p-3">
							{/* Stesse voci e stessi gruppi della barra laterale desktop (navGroups):
							    cambiare un gruppo qui o li' cambia entrambi. Gli atleti non hanno
							    gruppi e vedono la lista piatta. */}
							{(isCoachOrAdmin ? navGroups : [{ id: "all", label: null, links: navLinks }]).map(
								(group) =>
									group.links.length > 0 && (
										<div key={group.id} className="flex flex-col gap-0.5">
											{group.label && (
												<p className="px-3 pt-2 pb-1 text-[10px] uppercase tracking-widest text-muted-foreground">
													{group.label}
												</p>
											)}
											{group.links.map((link) => {
												const Icon = link.icon;
												const isActive = location.pathname === link.href;
												return (
													<Link
														key={link.href}
														to={link.href}
														onClick={() => setMobileMenuOpen(false)}
														className={`flex items-center gap-3 px-3 py-2.5 rounded-sm text-[15px] font-medium ${
															isActive
																? "bg-volt/10 text-volt"
																: "text-muted-foreground hover:text-foreground hover:bg-white/5"
														}`}
													>
														<Icon className="w-4.5 h-4.5" />
														<span className="whitespace-nowrap">{link.label}</span>
														{link.badge > 0 && (
															<span className="ml-auto min-w-[20px] h-5 px-1 flex items-center justify-center rounded-full bg-blaze text-white text-[11px] font-medium">
																{link.badge > 99 ? "99+" : link.badge}
															</span>
														)}
														{link.alert && (
															<span className="ml-auto h-4 w-4 flex items-center justify-center bg-amber-500 text-black text-[10px] font-bold rounded-full">
																!
															</span>
														)}
													</Link>
												);
											})}
										</div>
									)
							)}
							<div className="h-px bg-border my-1.5" />
							<div className="px-3 py-2 space-y-2.5">
								<div className="flex items-center justify-between gap-3">
									<div className="flex items-center gap-2 min-w-0">
										<Globe className="w-4.5 h-4.5 text-muted-foreground flex-shrink-0" />
										<p className="text-[11px] uppercase tracking-widest text-muted-foreground whitespace-nowrap">
											{t("languageLabel")}
										</p>
									</div>
									<LanguageToggle />
								</div>
								<div className="flex items-center justify-between gap-3">
									<div className="flex items-center gap-2 min-w-0">
										<Type className="w-4.5 h-4.5 text-muted-foreground flex-shrink-0" />
										<p className="text-[11px] uppercase tracking-widest text-muted-foreground whitespace-nowrap">
											{t("textSize")}
										</p>
									</div>
									<FontSizeToggle />
								</div>
								<div className="flex items-center justify-between gap-3">
									<div className="flex items-center gap-2 min-w-0">
										<SunMoon className="w-4.5 h-4.5 text-muted-foreground flex-shrink-0" />
										<p className="text-[11px] uppercase tracking-widest text-muted-foreground whitespace-nowrap">
											{t("themeLabel")}
										</p>
									</div>
									<ThemeToggle />
								</div>
							</div>
							<div className="h-px bg-border my-1.5" />
							<Link
								to="/settings"
								onClick={() => setMobileMenuOpen(false)}
								className="flex items-center justify-between gap-3 px-3 py-2.5 rounded-sm text-sm font-medium text-muted-foreground hover:text-foreground hover:bg-white/5"
							>
								<span className="flex items-center gap-3">
									<Settings className="w-4 h-4" />
									{t("settings")}
								</span>
							</Link>
							<button
								onClick={() => {
									setMobileMenuOpen(false);
									handleLogout();
								}}
								className="flex items-center gap-3 px-3 py-2.5 rounded-sm text-sm font-medium text-acid hover:bg-acid/10"
							>
								<LogOut className="w-5 h-5" />
								{t("logout")}
							</button>
						</nav>
					</div>
				)}
			</header>

			{/* Main Content — desktop sidebar (coach/admin) sits beside it */}
			<div className="flex-1 flex min-h-0">
				{isCoachOrAdmin && (
					<DesktopSidebar
						groups={navGroups}
						pinned={sidebarPinned}
						onPinnedChange={handleSidebarPinnedChange}
					/>
				)}
				<div className="flex-1 flex flex-col min-w-0 min-h-0">
					<main
						ref={mainRef}
						className={`main-content flex-1 overflow-y-auto ${
							user?.role === "athlete" ? "main-content-athlete" : ""
						}`}
						id="main-scroll-area"
					>
						{children}
					</main>

					{/* Footer - Only on desktop, non-fixed */}
					<footer className="hidden md:block bg-background border-t border-border py-2">
						<div className="max-w-7xl mx-auto px-6 flex items-center justify-between text-xs text-muted-foreground">
							<span>© 2026 Kraveon</span>
							<div className="flex items-center gap-4">
								<Link
									to="/privacy-policy"
									className="hover:text-volt transition-colors"
								>
									Privacy Policy
								</Link>
								<Link
									to="/cookie-policy"
									className="hover:text-volt transition-colors"
								>
									Cookie Policy
								</Link>
								<button
									onClick={() => window.openCookieSettings?.()}
									className="hover:text-volt transition-colors"
								>
									{t("manageCookies")}
								</button>
							</div>
						</div>
					</footer>
				</div>
			</div>

			{isCoachOrAdmin && (
				<CommandPalette
					open={commandOpen}
					onOpenChange={setCommandOpen}
					groups={navGroups}
					onLogout={handleLogout}
				/>
			)}

			<MobileInstallPrompt role={user?.role} />

			<PushOptInModal
				open={!pushOptIn.loading && pushOptIn.canPrompt && !pushOptIn.dismissed}
				onEnable={pushOptIn.enable}
				onDismiss={pushOptIn.dismiss}
			/>

			{/* Mobile Bottom Navigation - floating pill (Instagram-style) */}
			{!mobileMenuOpen && (
				<nav
					className={`mobile-nav md:hidden shrink-0 ${
						user?.role === "athlete" ? "mobile-nav-athlete" : ""
					}`}
				>
					<div className="relative flex items-center h-full">
						{/* Sliding active indicator (smooth Apple/Instagram-style transition) */}
						<div
							className="pointer-events-none absolute inset-y-[1px] left-[1px]"
							style={{
								width: `calc((100% - 2px) / ${mobileNavLinks.length})`,
								transform: `translateX(${
									(indicatorIndex < 0 ? 0 : indicatorIndex) * 100
								}%)`,
								opacity: indicatorIndex < 0 ? 0 : 1,
								transition:
									"transform 0.42s cubic-bezier(0.22, 1, 0.36, 1), opacity 0.25s ease",
							}}
						>
							<span
								className="block w-full h-full rounded-[27px]"
								style={{
									background: "rgba(0, 214, 143, 0.26)",
									boxShadow: "inset 0 0 0 1px rgba(0, 214, 143, 0.22)",
								}}
							/>
						</div>

						{mobileNavLinks.map((link) => {
							const Icon = link.icon;
							const isActive = location.pathname === link.href;
							return (
								<Link
									key={link.href}
									to={link.href}
									aria-label={link.label}
									className="relative z-10 flex-1 min-w-0 flex items-center justify-center h-full"
								>
									<span className="relative flex items-center justify-center">
										<Icon
											className={`w-[26px] h-[26px] shrink-0 transition-colors duration-200 ${
												isActive ? "text-volt" : "text-muted-foreground"
											}`}
										/>
										{link.badge > 0 && (
											<span className="absolute -top-2 -right-2.5 h-4 min-w-[16px] flex items-center justify-center bg-blaze text-white text-[9px] rounded-full px-1">
												{link.badge}
											</span>
										)}
										{link.alert && (
											<span className="absolute -top-2 -right-2.5 h-4 w-4 flex items-center justify-center bg-amber-500 text-black text-[9px] font-bold rounded-full">
												!
											</span>
										)}
									</span>
								</Link>
							);
						})}
					</div>
				</nav>
			)}
		</div>
	);
}
